import { useState } from "react"

import type { LoanActivityEventTypeDto } from "~/api/dto/active-loan.dto"

import {
  APPLICATION_ACTIVITY_HEADING,
  applicationStatusLabel,
  APPLY_FOR_FINANCING_LABEL,
  CANCEL_REPLACE_APPLICATION_LABEL,
  CONFIRM_REPLACE_APPLICATION_LABEL,
  FINANCING_CHANGED_LABEL,
  MARK_APPROVED_CANCEL_LABEL,
  MARK_APPROVED_CONFIRM_LABEL,
  MARK_APPROVED_CONFIRM_PROMPT,
  MARK_APPROVED_LABEL,
  PREVIOUS_APPLICATIONS_HEADING,
  REOPEN_APPLICATION_LABEL,
  REPLACE_APPLICATION_LABEL,
} from "./credit-human-copy"
import type {
  CreditHumanFinancingView,
  CreditHumanStatusBundle,
  ExistingLoanApplicationView,
  FinancingActionView,
} from "./financing-kind-bundle"

const amountUsd = new Intl.NumberFormat(`en-US`, {
  style: `currency`,
  currency: `USD`,
})

const dateLabel = (iso: string): string =>
  new Intl.DateTimeFormat(`en-US`, {
    year: `numeric`,
    month: `short`,
    day: `numeric`,
  }).format(new Date(iso))

const activityTypeLabel = (type: LoanActivityEventTypeDto): string => {
  switch (type) {
    case `application_submitted`:
      return `Application submitted`
    case `provider_decision`:
      return `Provider decision`
    case `loan_docs_generated`:
      return `Loan documents generated`
    case `loan_docs_sent`:
      return `Loan documents sent`
    case `customer_signed`:
      return `Customer signed`
    default: {
      const _exhaustive: never = type
      return _exhaustive
    }
  }
}

/**
 * Open the handoff tab during the click gesture so transient user activation
 * is not lost while Apply / Reopen / Replace awaits the network.
 */
function openHandoffDuringGesture(
  request: Promise<{ applicationUrl: string }>,
): Promise<void> {
  const applicationWindow = window.open(`about:blank`, `_blank`)
  if (applicationWindow) {
    applicationWindow.opener = null
  }

  return request
    .then(({ applicationUrl }) => {
      if (applicationWindow) {
        applicationWindow.location.href = applicationUrl
      } else {
        window.open(applicationUrl, `_blank`, `noopener,noreferrer`)
      }
    })
    .catch((error: unknown) => {
      applicationWindow?.close()
      throw error
    })
}

function ApplicationFinancingNote({
  bundle,
  application,
}: {
  bundle: CreditHumanStatusBundle
  application: ExistingLoanApplicationView
}): JSX.Element | null {
  if (application.matchesCurrentFinancing) {
    return null
  }
  return (
    <p
      data-testid={`${bundle.testIdPrefix}-application-financing-changed`}
      className="mt-1 text-[length:var(--step--2)] text-bp-gold"
    >
      {FINANCING_CHANGED_LABEL}
    </p>
  )
}

function FinancingSnapshot({
  bundle,
  application,
}: {
  bundle: CreditHumanStatusBundle
  application: ExistingLoanApplicationView
}): JSX.Element {
  const product = application.loanProduct
  return (
    <dl
      data-testid={`${bundle.testIdPrefix}-application-financing-snapshot`}
      className="mt-4 grid grid-cols-[minmax(7rem,auto)_1fr] gap-x-4 gap-y-2 text-[length:var(--step--2)]"
    >
      <dt className="text-bp-text-dim">Financed amount</dt>
      <dd className="text-bp-text tabular-nums">
        {amountUsd.format(application.amount)}
      </dd>
      <dt className="text-bp-text-dim">Loan Product</dt>
      <dd className="text-bp-text">{product?.name ?? `—`}</dd>
      <dt className="text-bp-text-dim">Term</dt>
      <dd className="text-bp-text">
        {product != null ? `${product.termYears}-year` : `—`}
      </dd>
      <dt className="text-bp-text-dim">APR</dt>
      <dd className="text-bp-text">
        {product?.apr != null ? `${product.apr.toFixed(2)}%` : `—`}
      </dd>
      <dt className="text-bp-text-dim">Dealer fee</dt>
      <dd className="text-bp-text">
        {product?.dealerFee != null ? `${product.dealerFee.toFixed(2)}%` : `—`}
      </dd>
      <dt className="text-bp-text-dim">Application date</dt>
      <dd className="text-bp-text">{dateLabel(application.createdAt)}</dd>
    </dl>
  )
}

function ApplicationActivity({
  bundle,
  applicationId,
}: {
  bundle: CreditHumanStatusBundle
  applicationId: string
}): JSX.Element {
  const activityQuery = bundle.creditHuman.useActivity(applicationId)
  const events = activityQuery.data ?? []

  return (
    <section
      aria-label={APPLICATION_ACTIVITY_HEADING}
      data-testid={`${bundle.testIdPrefix}-application-activity`}
      className="rounded-lg border border-bp-border bg-bp-elevated"
    >
      <h3 className="border-b border-bp-border px-4 py-3 text-[length:var(--step--1)] font-semibold text-bp-text">
        {APPLICATION_ACTIVITY_HEADING}
      </h3>
      {activityQuery.isLoading ? (
        <p className="px-4 py-3 text-[length:var(--step--2)] text-bp-text-muted">
          Loading activity…
        </p>
      ) : activityQuery.isError ? (
        <p
          role="alert"
          className="px-4 py-3 text-[length:var(--step--2)] text-bp-danger"
        >
          Unable to load application activity.
        </p>
      ) : events.length === 0 ? (
        <p className="px-4 py-3 text-[length:var(--step--2)] text-bp-text-muted">
          No activity recorded yet.
        </p>
      ) : (
        <ul className="divide-y divide-bp-border">
          {events.map((event) => (
            <li
              key={event.id}
              data-testid={`${bundle.testIdPrefix}-application-activity-event`}
              className="flex items-baseline justify-between gap-3 px-4 py-3 text-[length:var(--step--2)]"
            >
              <span className="text-bp-text">
                {activityTypeLabel(event.type)}
              </span>
              <span className="shrink-0 text-bp-text-muted">
                {dateLabel(event.occurredAt)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}

function PreviousApplications({
  bundle,
  applications,
}: {
  bundle: CreditHumanStatusBundle
  applications: readonly ExistingLoanApplicationView[]
}): JSX.Element {
  return (
    <section
      data-testid={`${bundle.testIdPrefix}-previous-applications`}
      className="rounded-lg border border-bp-border bg-bp-elevated"
    >
      <h3 className="border-b border-bp-border px-4 py-3 text-[length:var(--step--1)] font-semibold text-bp-text">
        {PREVIOUS_APPLICATIONS_HEADING}
      </h3>
      <ul className="divide-y divide-bp-border">
        {applications.map((application) => (
          <li
            key={application.id}
            data-testid={`${bundle.testIdPrefix}-previous-application`}
            className="px-4 py-3 text-[length:var(--step--2)] text-bp-text-muted"
          >
            <p>
              {[
                application.loanProduct?.name ?? `Loan Product`,
                amountUsd.format(application.amount),
                applicationStatusLabel(application.status),
                dateLabel(application.createdAt),
              ].join(` · `)}
            </p>
            <ApplicationFinancingNote
              bundle={bundle}
              application={application}
            />
          </li>
        ))}
      </ul>
    </section>
  )
}

/** The kind's Refresh Pricing under a refresh-required refusal, when it has one. */
function RefreshRequiredAction({
  bundle,
  proposalId,
  onRefreshed,
}: {
  bundle: CreditHumanStatusBundle
  proposalId: string
  onRefreshed: () => void
}): JSX.Element | null {
  const { RefreshPricing } = bundle.creditHuman
  return RefreshPricing ? (
    <RefreshPricing proposalId={proposalId} onRefreshed={onRefreshed} />
  ) : null
}

function ApplyForFinancingAction({
  bundle,
  proposalId,
  quoteId,
}: {
  bundle: CreditHumanStatusBundle
  proposalId: string
  quoteId: string
}): JSX.Element {
  const { isRefreshRequiredError, copy } = bundle.creditHuman
  const apply = bundle.creditHuman.useApply()
  const [idempotencyKey] = useState(() => crypto.randomUUID())
  const refreshRequired = apply.isError && isRefreshRequiredError(apply.error)

  return (
    <>
      {apply.isError ? (
        <p role="alert" className="text-[length:var(--step--2)] text-bp-danger">
          {refreshRequired
            ? copy.refreshRequired
            : `Unable to open the Credit Human application`}
        </p>
      ) : null}
      {refreshRequired ? (
        <RefreshRequiredAction
          bundle={bundle}
          proposalId={proposalId}
          onRefreshed={apply.reset}
        />
      ) : null}
      <button
        type="button"
        disabled={apply.isLoading}
        onClick={() => {
          void openHandoffDuringGesture(
            apply.mutateAsync({
              proposalId,
              body: { idempotencyKey, quoteId },
            }),
          ).catch(() => undefined)
        }}
        className="font-display inline-flex cursor-pointer items-center gap-2 rounded-full bg-bp-accent px-6 py-3 text-[length:var(--step-0)] font-bold text-bp-on-accent hover:bg-bp-accent-hover focus-visible:ring-2 focus-visible:ring-bp-accent focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50"
      >
        {apply.isLoading ? `Opening application` : APPLY_FOR_FINANCING_LABEL}
        <span aria-hidden="true">→</span>
      </button>
    </>
  )
}

function ReopenApplicationAction({
  bundle,
  proposalId,
  applicationId,
}: {
  bundle: CreditHumanStatusBundle
  proposalId: string
  applicationId: string
}): JSX.Element {
  const reopen = bundle.creditHuman.useReopen()

  return (
    <>
      {reopen.isError ? (
        <p role="alert" className="text-[length:var(--step--2)] text-bp-danger">
          {`Unable to reopen the Credit Human application`}
        </p>
      ) : null}
      <button
        type="button"
        disabled={reopen.isLoading}
        onClick={() => {
          void openHandoffDuringGesture(
            reopen.mutateAsync({ proposalId, applicationId }),
          ).catch(() => undefined)
        }}
        className="rounded-md bg-bp-accent px-4 py-2 text-[length:var(--step--1)] font-semibold text-bp-on-accent hover:bg-bp-accent-hover disabled:cursor-not-allowed disabled:opacity-50"
      >
        {reopen.isLoading ? `Reopening application` : REOPEN_APPLICATION_LABEL}
      </button>
    </>
  )
}

function MarkApprovedAction({
  bundle,
  proposalId,
  applicationId,
}: {
  bundle: CreditHumanStatusBundle
  proposalId: string
  applicationId: string
}): JSX.Element {
  const markApproved = bundle.creditHuman.useMarkApproved()
  const [confirming, setConfirming] = useState(false)

  if (confirming) {
    return (
      <div
        data-testid={`${bundle.testIdPrefix}-mark-approved-confirm`}
        className="max-w-md"
      >
        <p className="mb-3 text-[length:var(--step--1)] text-bp-text-muted">
          {MARK_APPROVED_CONFIRM_PROMPT}
        </p>
        {markApproved.isError ? (
          <p
            role="alert"
            className="mb-3 text-[length:var(--step--2)] text-bp-danger"
          >
            {`Unable to mark the application approved`}
          </p>
        ) : null}
        <div className="flex flex-wrap gap-3">
          <button
            type="button"
            disabled={markApproved.isLoading}
            onClick={() => {
              void markApproved
                .mutateAsync({ proposalId, applicationId })
                .then(() => {
                  setConfirming(false)
                })
                .catch(() => undefined)
            }}
            className="rounded-md bg-bp-accent px-4 py-2 text-[length:var(--step--1)] font-semibold text-bp-on-accent hover:bg-bp-accent-hover disabled:cursor-not-allowed disabled:opacity-50"
          >
            {markApproved.isLoading
              ? `Marking approved`
              : MARK_APPROVED_CONFIRM_LABEL}
          </button>
          <button
            type="button"
            disabled={markApproved.isLoading}
            onClick={() => {
              setConfirming(false)
            }}
            className="rounded-md bg-bp-elevated-2 px-4 py-2 text-[length:var(--step--1)] font-semibold text-bp-text hover:bg-bp-border disabled:cursor-not-allowed disabled:opacity-50"
          >
            {MARK_APPROVED_CANCEL_LABEL}
          </button>
        </div>
      </div>
    )
  }

  return (
    <button
      type="button"
      onClick={() => {
        setConfirming(true)
      }}
      className="rounded-md bg-bp-elevated-2 px-4 py-2 text-[length:var(--step--1)] font-semibold text-bp-text hover:bg-bp-border"
    >
      {MARK_APPROVED_LABEL}
    </button>
  )
}

function ReplaceApplicationAction({
  bundle,
  proposalId,
  applicationId,
  quoteId,
}: {
  bundle: CreditHumanStatusBundle
  proposalId: string
  applicationId: string
  quoteId: string
}): JSX.Element {
  const { isRefreshRequiredError, copy } = bundle.creditHuman
  const replace = bundle.creditHuman.useReplace()
  const [confirming, setConfirming] = useState(false)
  const [idempotencyKey, setIdempotencyKey] = useState(() => crypto.randomUUID())
  const refreshRequired =
    replace.isError && isRefreshRequiredError(replace.error)

  if (!confirming) {
    return (
      <button
        type="button"
        onClick={() => {
          setIdempotencyKey(crypto.randomUUID())
          setConfirming(true)
        }}
        className="rounded-md border border-bp-border-strong bg-transparent px-4 py-2 text-[length:var(--step--1)] font-semibold text-bp-text hover:bg-bp-elevated-2"
      >
        {REPLACE_APPLICATION_LABEL}
      </button>
    )
  }

  return (
    <div
      data-testid={`${bundle.testIdPrefix}-replace-application-confirm`}
      className="w-full rounded-md border border-bp-border bg-bp-elevated p-4"
    >
      <p className="text-[length:var(--step--1)] text-bp-text-muted">
        {`Replace this application with a new attempt from the current Quote? The prior application stays in history.`}
      </p>
      {replace.isError ? (
        <p
          role="alert"
          className="mt-3 text-[length:var(--step--2)] text-bp-danger"
        >
          {refreshRequired
            ? copy.refreshRequired
            : `Unable to replace the Credit Human application`}
        </p>
      ) : null}
      {refreshRequired ? (
        <RefreshRequiredAction
          bundle={bundle}
          proposalId={proposalId}
          onRefreshed={replace.reset}
        />
      ) : null}
      <div className="mt-3 flex flex-wrap gap-3">
        <button
          type="button"
          disabled={replace.isLoading}
          onClick={() => {
            void openHandoffDuringGesture(
              replace.mutateAsync({
                proposalId,
                applicationId,
                body: { idempotencyKey, quoteId },
              }),
            )
              .then(() => setConfirming(false))
              .catch(() => undefined)
          }}
          className="rounded-md bg-bp-accent px-4 py-2 text-[length:var(--step--1)] font-semibold text-bp-on-accent hover:bg-bp-accent-hover disabled:cursor-not-allowed disabled:opacity-50"
        >
          {replace.isLoading
            ? `Replacing application`
            : CONFIRM_REPLACE_APPLICATION_LABEL}
        </button>
        <button
          type="button"
          disabled={replace.isLoading}
          onClick={() => setConfirming(false)}
          className="rounded-md border border-bp-border-strong bg-transparent px-4 py-2 text-[length:var(--step--1)] font-semibold text-bp-text hover:bg-bp-elevated-2 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {CANCEL_REPLACE_APPLICATION_LABEL}
        </button>
      </div>
    </div>
  )
}

function ApplicationActions({
  bundle,
  proposalId,
  quoteId,
  currentApplication,
  applyAction,
  reopenAction,
  markApprovedAction,
  replaceAction,
}: {
  bundle: CreditHumanStatusBundle
  proposalId: string
  quoteId: string | null
  currentApplication: ExistingLoanApplicationView | null
  applyAction: FinancingActionView
  reopenAction: FinancingActionView
  markApprovedAction: FinancingActionView
  replaceAction: FinancingActionView
}): JSX.Element | null {
  const canApply = applyAction.allowed && quoteId != null
  const canReplace =
    replaceAction.allowed && currentApplication != null && quoteId != null
  const canReopen = reopenAction.allowed && currentApplication != null
  const canMarkApproved =
    markApprovedAction.allowed && currentApplication != null

  if (!canApply && !canReopen && !canMarkApproved && !canReplace) {
    return null
  }

  return (
    <div
      data-testid={`${bundle.testIdPrefix}-application-actions`}
      className="flex flex-col items-start gap-3"
    >
      {canApply && quoteId != null ? (
        <ApplyForFinancingAction
          bundle={bundle}
          proposalId={proposalId}
          quoteId={quoteId}
        />
      ) : null}
      {canReopen && currentApplication != null ? (
        <ReopenApplicationAction
          bundle={bundle}
          proposalId={proposalId}
          applicationId={currentApplication.id}
        />
      ) : null}
      {canMarkApproved && currentApplication != null ? (
        <MarkApprovedAction
          bundle={bundle}
          proposalId={proposalId}
          applicationId={currentApplication.id}
        />
      ) : null}
      {canReplace && quoteId != null && currentApplication != null ? (
        <ReplaceApplicationAction
          bundle={bundle}
          proposalId={proposalId}
          applicationId={currentApplication.id}
          quoteId={quoteId}
        />
      ) : null}
    </div>
  )
}

/**
 * Credit Human Existing Loan Application status for any proposal kind. The
 * kind's bundle supplies the commands, the activity read and the refresh
 * affordance; this owns the snapshot, history and action rules.
 */
export function CreditHumanFinancingStatus({
  bundle,
  proposalId,
  financing,
}: {
  bundle: CreditHumanStatusBundle
  proposalId: string
  financing: CreditHumanFinancingView
}): JSX.Element {
  const { currentApplication, previousApplications, quote, actions } = financing
  const { testIdPrefix } = bundle

  return (
    <div
      data-testid={`${testIdPrefix}-credit-human-financing-status`}
      className="flex flex-1 flex-col gap-4"
    >
      {currentApplication != null ? (
        <section
          data-testid={`${testIdPrefix}-current-application`}
          className="rounded-lg border border-bp-border bg-bp-elevated"
        >
          <div className="border-b border-bp-border px-4 py-3">
            <p
              data-testid={`${testIdPrefix}-application-status`}
              className="text-[length:var(--step--1)] font-semibold text-bp-text"
            >
              {`Application status: ${applicationStatusLabel(currentApplication.status)}`}
            </p>
            <ApplicationFinancingNote
              bundle={bundle}
              application={currentApplication}
            />
          </div>
          <div className="px-4 py-4">
            <FinancingSnapshot
              bundle={bundle}
              application={currentApplication}
            />
          </div>
        </section>
      ) : (
        <p
          data-testid={`${testIdPrefix}-no-current-application`}
          className="text-[length:var(--step--1)] text-bp-text-muted"
        >
          No Existing Loan Application yet.
        </p>
      )}

      <ApplicationActions
        bundle={bundle}
        proposalId={proposalId}
        quoteId={quote?.id ?? null}
        currentApplication={currentApplication}
        applyAction={actions.apply}
        reopenAction={actions.reopen}
        markApprovedAction={actions.markApproved}
        replaceAction={actions.replace}
      />

      {currentApplication != null ? (
        <ApplicationActivity
          bundle={bundle}
          applicationId={currentApplication.id}
        />
      ) : null}

      {previousApplications.length > 0 ? (
        <PreviousApplications
          bundle={bundle}
          applications={previousApplications}
        />
      ) : null}
    </div>
  )
}
